import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Range } from './styles';
import useWidthMeasurement from '__hooks/useWidthMeasurement';
import { formatBillingCurrency } from '../../util';

type EstimatorProps = {
  period: string;
  periodAbbreviated: string;
  entityPlural: string;
  start: number;
  end: number;
  mode?: 'linear' | 'exponential';
  unitSize: number;
  unitPrice: number;
  cta?: any;
  isMax?: boolean;
};

/*
  These intervals get evenly spaced throughout the slider. So, if there are 4 values,
  the first 25% of the slider will use intervals[0] as the step value, the next 25% will use intervals[1], etc.
  This leads to a quasi-exponential slider, where the values increase more rapidly as you near the top end,
  but it's more configurable than a straight exponential growth.

  Note: if you're reading this in the future and trying to switch this to a linear scale,
  don't modify any of this. There is a prop to <Estimator /> called `mode` that allows you to do that.
*/
const intervals = [10, 50, 100, 200];

/*
  Provides a function to transform a slider positional value (linear) to the actual value,
  based on where in `intervals` the value lies.
*/
const transformScale = (
  min: number,
  max: number
): [number, (input: number) => number] => {
  const distributions = intervals.length;
  const descretePoints = Math.ceil(
    (max - min) /
      intervals.reduce((total, step) => total + step / distributions, 0)
  );

  return [
    descretePoints,
    (input: number) => {
      const stepTransforms = intervals.map((s, i) => {
        const setCount = Math.min(
          Math.ceil(input - (descretePoints * i) / distributions),
          Math.round(descretePoints / distributions)
        );
        return setCount > 0 ? setCount * s : 0;
      });

      let lastStep = 0;
      const out =
        Math.round(
          stepTransforms.reduce((total, num, i) => {
            if (num) {
              lastStep = i;
            }
            return total + num;
          })
        ) + min;

      const currentUnit = intervals[lastStep];
      return Math.min(Math.round(out / currentUnit) * currentUnit, max);
    },
  ];
};

const thumbWidth = 14;

export const Estimator = (props: EstimatorProps) => {
  const {
    period,
    entityPlural,
    start,
    end,
    mode = 'exponential',
    unitSize,
    unitPrice,
    periodAbbreviated,
    cta,
    isMax,
  } = props;

  const { ref, width: tooltipWidth } = useWidthMeasurement();

  const [sliderValue, setSliderValue] = useState(
    mode === 'exponential' ? 0 : start
  );

  const { t } = useTranslation();

  const [points, transform] = transformScale(start, end);

  const estimateValue = useMemo(() => {
    if (mode === 'exponential') {
      return transform(sliderValue);
    }

    return sliderValue;
  }, [mode, sliderValue, transform]);

  const sliderStart = mode === 'exponential' ? 0 : start;
  const sliderEnd = mode === 'exponential' ? points : end;

  const numUnits = Math.ceil((estimateValue - start) / unitSize);
  const cost = numUnits * unitPrice;

  return (
    <div className="flex flex-col">
      <Typography>
        {t('{{period}} Usage-Based Billing Estimate', { period })}
      </Typography>
      <Spacer size={20} />
      <Spacer size={20} />
      <div className="flex items-center gap-x-4">
        <div className="pr-2">
          <Typography size="sm">{start.toLocaleString()}</Typography>
          <Spacer size={5} />
          <Typography size="sm">{entityPlural}</Typography>
        </div>
        <div className="grow relative">
          <Range
            type="range"
            min={sliderStart}
            max={sliderEnd}
            className="w-full"
            value={sliderValue}
            onChange={(e) => {
              setSliderValue(e.target.valueAsNumber);
            }}
            thumbWidth={thumbWidth}
          />
          <div
            className="absolute left-[calc(var(--thumb-width)/2)] right-[calc(var(--thumb-width)/2)] top-0 bottom-0 pointer-events-none"
            style={
              {
                '--thumb-width': `${thumbWidth}px`,
              } as CSSProperties
            }
          >
            <div
              ref={ref}
              className="absolute top-[-30px] left-[calc(var(--slider-percent)-(var(--tooltip-width)/2))] border border-border-medium rounded-md py-2 px-3 bg-white z-10"
              style={
                {
                  '--slider-percent': `${((sliderValue - sliderStart) / (sliderEnd - sliderStart)) * 100}%`,
                  '--tooltip-width': `${tooltipWidth}px`,
                } as CSSProperties
              }
            >
              <Typography size="sm">
                {estimateValue.toLocaleString()}
              </Typography>
            </div>
          </div>
        </div>
        <div className="pl-2 flex flex-col items-end">
          <Typography size="sm">
            {end.toLocaleString()}
            {isMax ? '+' : ''}
          </Typography>
          <Spacer size={5} />
          <Typography size="sm">{entityPlural}</Typography>
        </div>
      </div>
      <Spacer size={10} />
      {estimateValue === end ? (
        <div className="flex self-center items-end min-h-[13px]">{cta}</div>
      ) : (
        <div className="flex self-center items-end min-h-[13px]">
          <Typography size="lg">
            +{formatBillingCurrency(cost, true)}
          </Typography>
          <Typography>/{periodAbbreviated}</Typography>
        </div>
      )}
    </div>
  );
};
